import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, Inbox, PenSquare, Settings, LogOut, Save, Download, ExternalLink, Image as ImageIcon, History, Send, RotateCcw, Upload, X, Check, Users, DollarSign, UserPlus, BarChart3, Eye, EyeOff, Quote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API, apiError } from "@/lib/api";
import ClientsPanel from "@/components/admin/ClientsPanel";
import ChargesPanel from "@/components/admin/ChargesPanel";
import ImageCropper from "@/components/admin/ImageCropper";
import ReportsPanel from "@/components/admin/ReportsPanel";
import TestimonialsPanel from "@/components/admin/TestimonialsPanel";

const TABS = [
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "clients", label: "Clients", icon: Users },
  { key: "submissions", label: "Signed Docs", icon: PenSquare },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "charges", label: "Charges", icon: DollarSign },
  { key: "testimonials", label: "Testimonials", icon: Quote },
  { key: "branding", label: "Branding", icon: ImageIcon },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATUSES = ["Received", "In Progress", "Recovered", "Closed"];
const STATUS_STYLE = {
  "Received": "bg-[#F5F3EC] text-[#58635C] border-[#E5E1D8]",
  "In Progress": "bg-[#FBF2DE] text-[#8A6D2F] border-[#E8D9B0]",
  "Recovered": "bg-[#E7F0E9] text-[#3F6B4E] border-[#C6DDCC]",
  "Closed": "bg-[#0A0D0B] text-white border-[#0A0D0B]",
};

const REPLY_TEMPLATES = [
  { label: "Acknowledge enquiry", text: "Thank you for your enquiry. We have received the details and one of our team will review your matter and be in touch shortly with the next steps." },
  { label: "Request more information", text: "Thank you for getting in touch. To progress your matter, could you please provide the following:\n\n- Copies of the relevant invoices or agreement\n- The debtor's full name and last known address\n- A summary of any communication to date\n\nOnce we have these we can advise on the best course of action." },
  { label: "Quote sent", text: "Thank you for your enquiry. Please find our indicative Schedule of Charges attached/linked. Our fees are based on the work required and we will keep you informed at every stage. Let us know if you'd like to proceed and we'll open a file straight away." },
];

const IMAGE_META = [
  { key: "hero", label: "Hero background", hint: "Large banner behind the home headline", aspect: 16 / 9 },
  { key: "credit", label: "Credit Recovery", hint: "Services section image", aspect: 16 / 10 },
  { key: "serving", label: "Document Serving", hint: "Services section image", aspect: 16 / 10 },
  { key: "debt", label: "Debt Management", hint: "Services section image", aspect: 16 / 10 },
  { key: "portrait", label: "Principal portrait", hint: "Photo of Paul Kyle in the About section", aspect: 3 / 4 },
  { key: "emblem", label: "Brand emblem", hint: "Decorative brand artwork", aspect: 1 },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reports");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  if (loading || !user) {
    return <div className="light-scope flex items-center justify-center min-h-screen"><Loader2 className="animate-spin text-[#B89874]" /></div>;
  }

  return (
    <div className="light-scope min-h-screen flex flex-col md:flex-row">
      <aside className="md:w-64 bg-white border-b md:border-b-0 md:border-r border-[#E5E1D8] md:min-h-screen p-6 flex md:flex-col justify-between">
        <div>
          <div className="font-display text-3xl tracking-tight text-[#0A0D0B]">Grafton<span className="text-[#B89874]">.</span></div>
          <p className="hidden md:block mt-1 text-[10px] uppercase tracking-[0.24em] text-[#B89874]">Admin Portal</p>
          <nav className="hidden md:flex flex-col gap-1 mt-10">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    tab === t.key ? "bg-[#0A0D0B] text-white" : "text-[#58635C] hover:bg-[#F5F3EC]"
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="hidden md:flex flex-col gap-1">
          <a href="/" target="_blank" rel="noreferrer" data-testid="view-site-btn"
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#58635C] hover:text-[#0A0D0B]">
            <ExternalLink size={16} /> View public site
          </a>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#58635C] hover:text-[#0A0D0B]">
            <LogOut size={16} /> Sign out
          </button>
        </div>
        {/* mobile tabs */}
        <select value={tab} onChange={(e) => setTab(e.target.value)} className="md:hidden border border-[#E5E1D8] px-3 py-2">
          {TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </aside>

      <main className="flex-1 p-6 md:p-12 max-w-[1100px]">
        {tab === "reports" && <ReportsPanel />}
        {tab === "documents" && <DocsPanel />}
        {tab === "clients" && <ClientsPanel />}
        {tab === "submissions" && <SubmissionsPanel />}
        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "charges" && <ChargesPanel />}
        {tab === "testimonials" && <TestimonialsPanel />}
        {tab === "branding" && <BrandingPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

function DocsPanel() {
  const [docs, setDocs] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState(null);
  const [showVersions, setShowVersions] = useState(false);

  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data.documents)); }, []);

  const open = (d) => { setSel(d); setForm({ title: d.title, description: d.description, body: d.body }); setShowVersions(false); setVersions(null); };

  const togglePublish = async (d, e) => {
    e.stopPropagation();
    const next = d.published === false;
    try {
      await api.patch(`/documents/${d.slug}/publish`, { published: next });
      setDocs((ds) => ds.map((x) => (x.slug === d.slug ? { ...x, published: next } : x)));
      setSel((s) => (s && s.slug === d.slug ? { ...s, published: next } : s));
      toast.success(next ? "Now shown on the public page." : "Hidden from the public page.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/documents/${sel.slug}`, form);
      setDocs((ds) => ds.map((d) => (d.slug === sel.slug ? data : d)));
      setSel(data);
      toast.success("Document saved. A version snapshot was kept.");
      if (showVersions) loadVersions(data.slug);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const loadVersions = async (slug) => {
    const { data } = await api.get(`/documents/${slug}/versions`);
    setVersions(data.versions);
  };

  const toggleVersions = () => {
    const next = !showVersions;
    setShowVersions(next);
    if (next && versions === null) loadVersions(sel.slug);
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Restore this version? Your current text will be saved as a new version first.")) return;
    try {
      const { data } = await api.post(`/documents/${sel.slug}/rollback/${versionId}`);
      setDocs((ds) => ds.map((d) => (d.slug === sel.slug ? data : d)));
      setSel(data);
      setForm({ title: data.title, description: data.description, body: data.body });
      toast.success("Version restored.");
      loadVersions(sel.slug);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Documents</h1>
      <p className="mt-2 text-[#58635C]">Edit any document, and choose which ones appear on the public Document Centre. Hidden documents can still be used to generate letters.</p>
      <div className="mt-8 grid md:grid-cols-[300px_1fr] gap-6">
        <div className="space-y-1 max-h-[70vh] overflow-y-auto border border-[#E5E1D8] bg-white">
          {docs.map((d) => (
            <div key={d.slug} className={`flex items-center border-b border-[#F0EDE4] transition-colors ${sel?.slug === d.slug ? "bg-[#F5F3EC]" : "hover:bg-[#FBFAF5]"}`}>
              <button data-testid={`admin-doc-${d.slug}`} onClick={() => open(d)}
                className="flex-1 min-w-0 text-left px-4 py-3 text-sm">
                <div className={`font-medium truncate ${d.published === false ? "text-[#98A09A]" : "text-[#0A0D0B]"}`}>{d.title}</div>
                <div className="text-[11px] uppercase tracking-wide text-[#B89874] flex items-center gap-1.5">
                  {d.category}
                  {d.published === false && <span className="text-[#98A09A] normal-case tracking-normal">· hidden</span>}
                </div>
              </button>
              <button onClick={(e) => togglePublish(d, e)} data-testid={`doc-publish-toggle-${d.slug}`}
                title={d.published === false ? "Show on public page" : "Hide from public page"}
                className={`shrink-0 mr-2 p-2 transition-colors ${d.published === false ? "text-[#98A09A] hover:text-[#3F6B4E]" : "text-[#3F6B4E] hover:text-[#0A0D0B]"}`}>
                {d.published === false ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          ))}
        </div>
        <div>
          {!sel ? (
            <div className="border border-dashed border-[#E5E1D8] p-12 text-center text-[#58635C]">Select a document to edit.</div>
          ) : (
            <div className="bg-white border border-[#E5E1D8] p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-[#F0EDE4]">
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Public visibility</span>
                <button onClick={(e) => togglePublish(sel, e)} data-testid="doc-publish-toggle-editor"
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${sel.published === false
                    ? "border-[#E5E1D8] text-[#98A09A] hover:border-[#3F6B4E] hover:text-[#3F6B4E]"
                    : "border-[#3F6B4E] text-[#3F6B4E] hover:bg-[#3F6B4E] hover:text-white"}`}>
                  {sel.published === false ? <><EyeOff size={15} /> Hidden from public page</> : <><Eye size={15} /> Shown on public page</>}
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Title</label>
                <input data-testid="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Description</label>
                <input data-testid="edit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Body</label>
                <textarea data-testid="edit-body" rows={16} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none font-mono-gg text-sm leading-relaxed resize-y" />
                <span className="text-xs text-[#58635C]">Tip: leave a blank line between paragraphs. Use [placeholders] for template fields.</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={save} disabled={saving} data-testid="edit-save"
                  className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                </button>
                <button onClick={toggleVersions} data-testid="edit-history"
                  className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                  <History size={15} /> History
                </button>
                <a href={`${API}/documents/${sel.slug}/pdf`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-6 py-3 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors">
                  <Download size={15} /> Preview PDF
                </a>
                <a href={`${API}/documents/${sel.slug}/docx`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                  <Download size={15} /> Word
                </a>
              </div>

              {showVersions && (
                <div className="mt-2 border-t border-[#E5E1D8] pt-4" data-testid="version-history">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C] mb-3">Version history</div>
                  {versions === null ? (
                    <Loader2 size={16} className="animate-spin text-[#B89874]" />
                  ) : versions.length === 0 ? (
                    <p className="text-sm text-[#58635C]">No previous versions yet. Each save creates a snapshot you can restore.</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {versions.map((v) => (
                        <div key={v.id} data-testid={`version-${v.id}`} className="flex items-center justify-between gap-3 bg-[#FBFAF5] border border-[#F0EDE4] px-4 py-2.5">
                          <div className="min-w-0">
                            <div className="text-sm text-[#0A0D0B] truncate">{v.title}</div>
                            <div className="text-xs text-[#B89874] font-mono-gg">{new Date(v.saved_at).toLocaleString("en-NZ")}</div>
                          </div>
                          <button onClick={() => rollback(v.id)} data-testid={`rollback-${v.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-3 py-1.5 hover:bg-[#0A0D0B] hover:text-white transition-colors shrink-0">
                            <RotateCcw size={13} /> Restore
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmissionsPanel() {
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState("All");
  useEffect(() => { api.get("/submissions").then((r) => setSubs(r.data.submissions)); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/submissions/${id}/status`, { status });
      setSubs((ss) => ss.map((s) => (s.id === id ? { ...s, status } : s)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const counts = (subs || []).reduce((acc, s) => { const k = s.status || "Received"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const filtered = (subs || []).filter((s) => filter === "All" || (s.status || "Received") === filter);

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Signed Documents</h1>
      <p className="mt-2 text-[#58635C]">Letters of Engagement completed and e-signed by clients. Track each file from received through to recovered.</p>
      {!subs ? <Loader2 className="animate-spin text-[#B89874] mt-8" /> : subs.length === 0 ? (
        <Empty text="No signed documents yet." />
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="status-filters">
            {["All", ...STATUSES].map((f) => {
              const n = f === "All" ? subs.length : (counts[f] || 0);
              return (
                <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-xs font-semibold px-3.5 py-2 border transition-colors ${filter === f ? "bg-[#0A0D0B] text-white border-[#0A0D0B]" : "bg-white text-[#58635C] border-[#E5E1D8] hover:border-[#0A0D0B] hover:text-[#0A0D0B]"}`}>
                  {f} <span className="opacity-70">({n})</span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 space-y-3">
            {filtered.length === 0 ? <Empty text={`No documents with status "${filter}".`} /> : filtered.map((s) => {
              const status = s.status || "Received";
              return (
                <div key={s.id} data-testid={`submission-${s.id}`} className="bg-white border border-[#E5E1D8] p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-[#0A0D0B]">{s.company_name || "—"} · <span className="text-[#58635C]">{s.contact_name}</span></span>
                      <span data-testid={`status-badge-${s.id}`} className={`text-[10px] uppercase tracking-wide px-2.5 py-1 border ${STATUS_STYLE[status]}`}>{status}</span>
                    </div>
                    <div className="text-sm text-[#58635C]">{s.title} · {s.signer_email}</div>
                    <div className="text-xs text-[#B89874] font-mono-gg mt-1">
                      {s.reference && <span className="text-[#0A0D0B] mr-3">Ref {s.reference}</span>}
                      {new Date(s.created_at).toLocaleString("en-NZ")}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select value={status} onChange={(e) => changeStatus(s.id, e.target.value)} data-testid={`status-select-${s.id}`}
                      className="border border-[#E5E1D8] px-3 py-2.5 text-sm text-[#0A0D0B] focus:border-[#B89874] outline-none bg-white">
                      {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <a href={`${API}/submissions/${s.id}/pdf`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-5 py-2.5 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors">
                      <Download size={15} /> PDF
                    </a>
                    <a href={`${API}/submissions/${s.id}/docx`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-5 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                      <Download size={15} /> Word
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function EnquiriesPanel() {
  const [quotes, setQuotes] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/quotes").then((r) => setQuotes(r.data.quotes)); }, []);

  const openReply = (q) => { setReplyTo(q); setMsg(""); };

  const convert = async (q) => {
    if (q.converted_client_id) return toast.info("This enquiry has already been converted.");
    if (!window.confirm(`Create a client file from ${q.name}'s enquiry?`)) return;
    try {
      await api.post(`/quotes/${q.id}/convert`);
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, converted_client_id: "done" } : x)));
      toast.success("Client created — open the Clients tab to view it.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const sendReply = async () => {
    if (!msg.trim()) return toast.error("Please write a message");
    setSending(true);
    try {
      await api.post(`/quotes/${replyTo.id}/reply`, { message: msg });
      setQuotes((qs) => qs.map((q) => (q.id === replyTo.id ? { ...q, replied_at: new Date().toISOString(), reply_message: msg } : q)));
      toast.success(`Reply sent to ${replyTo.email}.`);
      setReplyTo(null); setMsg("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Enquiries</h1>
      <p className="mt-2 text-[#58635C]">Quote requests submitted through the website. Reply directly with a branded email.</p>
      {!quotes ? <Loader2 className="animate-spin text-[#B89874] mt-8" /> : quotes.length === 0 ? (
        <Empty text="No enquiries yet." />
      ) : (
        <div className="mt-8 space-y-3">
          {quotes.map((q) => (
            <div key={q.id} data-testid={`quote-${q.id}`} className="bg-white border border-[#E5E1D8] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="font-medium text-[#0A0D0B] flex items-center gap-2">
                  {q.name} {q.company && <span className="text-[#58635C]">· {q.company}</span>}
                  {q.replied_at && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#3F6B4E] bg-[#E7F0E9] border border-[#C6DDCC] px-2 py-0.5"><Check size={11} /> Replied</span>}
                </div>
                <div className="flex items-center gap-2">
                  {q.reason && <span className={`text-xs px-3 py-1 uppercase tracking-wide ${q.reason === "Debtor reply" ? "bg-[#FBF2DE] text-[#8A6D2F]" : q.reason === "Quote" ? "bg-[#E7F0E9] text-[#3F6B4E]" : "bg-[#0A0D0B] text-[#F5F3EC]"}`}>{q.reason}</span>}
                  <span className="text-xs px-3 py-1 bg-[#F5F3EC] text-[#B89874] uppercase tracking-wide">{q.service}</span>
                </div>
              </div>
              <div className="text-sm text-[#58635C] mt-1">
                <a href={`mailto:${q.email}`} className="underline">{q.email}</a> {q.phone && `· ${q.phone}`} {q.amount && `· Amount: ${q.amount}`} {q.reference && <span className="text-[#0A0D0B] font-semibold">· Ref {q.reference}</span>}
              </div>
              {q.message && <p className="mt-3 text-sm text-[#0A0D0B] leading-relaxed">{q.message}</p>}
              <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                <div className="text-xs text-[#B89874] font-mono-gg">{new Date(q.created_at).toLocaleString("en-NZ")}</div>
                <div className="flex gap-2">
                  <button onClick={() => convert(q)} data-testid={`convert-btn-${q.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold border border-[#B89874] text-[#8A6D2F] px-4 py-2 hover:bg-[#B89874] hover:text-[#0A0D0B] transition-colors">
                    <UserPlus size={14} /> {q.converted_client_id ? "Converted" : "Convert to client"}
                  </button>
                  <button onClick={() => openReply(q)} data-testid={`reply-btn-${q.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-4 py-2 hover:bg-[#0A0D0B] hover:text-white transition-colors">
                    <Send size={14} /> {q.replied_at ? "Reply again" : "Reply"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {replyTo && (
        <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 bg-[#0A0D0B]/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-xl border border-[#E5E1D8] my-8" data-testid="reply-modal">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E1D8]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#B89874]">Reply to enquiry</div>
                <h3 className="font-display text-2xl text-[#0A0D0B]">{replyTo.name}</h3>
                <div className="text-sm text-[#58635C]">{replyTo.email}</div>
              </div>
              <button onClick={() => setReplyTo(null)} data-testid="reply-close" className="p-2 text-[#58635C] hover:text-[#0A0D0B]"><X size={20} /></button>
            </div>
            <div className="px-6 py-6 space-y-3">
              <div className="flex flex-wrap gap-2" data-testid="reply-templates">
                {REPLY_TEMPLATES.map((t) => (
                  <button key={t.label} onClick={() => setMsg(t.text)} data-testid={`reply-template-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-xs font-semibold px-3 py-1.5 border border-[#E5E1D8] text-[#58635C] hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                    {t.label}
                  </button>
                ))}
              </div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Your message</label>
              <textarea data-testid="reply-message" rows={8} value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="Kia ora, thank you for your enquiry…"
                className="w-full border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none text-sm leading-relaxed resize-y" />
              <p className="text-xs text-[#58635C]">Tap a template above to start, then personalise. This sends a branded Grafton Group email to the client, with replies routed to {`graftongroup@chartwell.co.nz`}.</p>
            </div>
            <div className="px-6 py-5 border-t border-[#E5E1D8] flex justify-end gap-3">
              <button onClick={() => setReplyTo(null)} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
              <button onClick={sendReply} disabled={sending} data-testid="reply-send"
                className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-7 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BrandingPanel() {
  const [images, setImages] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [cropping, setCropping] = useState(null); // { key, src, aspect, label }
  const fileRefs = useRef({});

  const load = () => api.get("/settings/images").then((r) => setImages(r.data.images));
  useEffect(() => { load(); }, []);

  const pick = (key) => fileRefs.current[key] && fileRefs.current[key].click();

  const onFile = (m, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    const reader = new FileReader();
    reader.onload = () => setCropping({ key: m.key, src: reader.result, aspect: m.aspect, label: m.label });
    reader.readAsDataURL(file);
  };

  const uploadBlob = async (key, blob) => {
    setCropping(null);
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `${key}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post(`/settings/images/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((im) => ({ ...im, [key]: `${data.url}?t=${Date.now()}` }));
      toast.success("Image updated. It's now live on the website.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await api.post(`/settings/images/${key}/reset`);
      setImages((im) => ({ ...im, [key]: `${data.url}?t=${Date.now()}` }));
      toast.success("Reset to the original image.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Branding</h1>
      <p className="mt-2 text-[#58635C]">Replace any image on the public website. You can crop and reposition before it goes live. Changes apply immediately.</p>
      {!images ? <Loader2 className="animate-spin text-[#B89874] mt-8" /> : (
        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {IMAGE_META.map((m) => (
            <div key={m.key} data-testid={`image-card-${m.key}`} className="bg-white border border-[#E5E1D8] p-4">
              <div className="aspect-[16/10] overflow-hidden bg-[#F5F3EC] border border-[#F0EDE4]">
                <img src={images[m.key]} alt={m.label} data-testid={`image-preview-${m.key}`} className="w-full h-full object-cover" />
              </div>
              <div className="mt-3">
                <div className="font-medium text-[#0A0D0B]">{m.label}</div>
                <div className="text-xs text-[#58635C]">{m.hint}</div>
              </div>
              <input ref={(el) => (fileRefs.current[m.key] = el)} type="file" accept="image/*" className="hidden"
                data-testid={`image-input-${m.key}`}
                onChange={(e) => { onFile(m, e.target.files[0]); e.target.value = ""; }} />
              <div className="mt-3 flex gap-2">
                <button onClick={() => pick(m.key)} disabled={busyKey === m.key} data-testid={`image-upload-${m.key}`}
                  className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#233027] transition-colors disabled:opacity-60">
                  {busyKey === m.key ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Replace
                </button>
                <button onClick={() => reset(m.key)} disabled={busyKey === m.key} data-testid={`image-reset-${m.key}`}
                  className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors disabled:opacity-60">
                  <RotateCcw size={14} /> Reset
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {cropping && (
        <ImageCropper src={cropping.src} aspect={cropping.aspect} label={cropping.label}
          onCancel={() => setCropping(null)} onDone={(blob) => uploadBlob(cropping.key, blob)} />
      )}
    </div>
  );
}

function SettingsPanel() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [signoff, setSignoff] = useState("");
  const [savingSignoff, setSavingSignoff] = useState(false);
  const [remEnabled, setRemEnabled] = useState(true);
  const [savingRem, setSavingRem] = useState(false);

  useEffect(() => { api.get("/settings/general").then((r) => { setSignoff(r.data.letter_signoff || ""); setRemEnabled(r.data.settlement_reminders_enabled !== false); }); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== nw2) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated.");
      setCur(""); setNw(""); setNw2("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveSignoff = async () => {
    setSavingSignoff(true);
    try {
      await api.put("/settings/general", { letter_signoff: signoff, settlement_reminders_enabled: remEnabled });
      toast.success("Letter sign-off saved.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingSignoff(false);
    }
  };

  const toggleReminders = async () => {
    const next = !remEnabled;
    setRemEnabled(next);
    setSavingRem(true);
    try {
      await api.put("/settings/general", { letter_signoff: signoff, settlement_reminders_enabled: next });
      toast.success(next ? "Settlement reminders turned on." : "Settlement reminders turned off.");
    } catch (err) {
      setRemEnabled(!next);
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingRem(false);
    }
  };

  return (
    <div className="max-w-lg">
      <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Settings</h1>
      <p className="mt-2 text-[#58635C]">Manage your admin password and generated-letter sign-off.</p>

      <div className="mt-8 bg-white border border-[#E5E1D8] p-6" data-testid="signoff-card">
        <h2 className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Letter sign-off</h2>
        <p className="mt-1 text-sm text-[#58635C]">Added to the end of every generated recovery letter. Paul's signature is inserted automatically after the first line. Your phone, email and bank details already appear in the letter footer, so leave them out here to avoid doubling up.</p>
        <textarea data-testid="signoff-input" value={signoff} onChange={(e) => setSignoff(e.target.value)} rows={5}
          className="mt-4 w-full border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y font-mono-gg text-sm" />
      </div>

      <div className="mt-6 bg-white border border-[#E5E1D8] p-6" data-testid="reminders-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Settlement follow-ups</h2>
            <p className="mt-1 text-sm text-[#58635C]">Automatically email the debtor a reminder if an accepted settlement isn't paid by its due date (repeats weekly until paid).</p>
          </div>
          <button onClick={toggleReminders} disabled={savingRem} role="switch" aria-checked={remEnabled} data-testid="reminders-toggle"
            className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${remEnabled ? "bg-[#3F6B4E]" : "bg-[#CFC9BC]"} disabled:opacity-60`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${remEnabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
        <div className="mt-3 text-xs font-semibold" data-testid="reminders-state">
          {remEnabled ? <span className="text-[#3F6B4E]">Reminders are ON</span> : <span className="text-[#58635C]">Reminders are OFF</span>}
        </div>
      </div>

      <form onSubmit={submit} data-testid="change-pw-form" className="mt-6 bg-white border border-[#E5E1D8] p-6 space-y-4">
        <h2 className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Change password</h2>
        {[["Current password", cur, setCur, "cur"], ["New password", nw, setNw, "new"], ["Confirm new password", nw2, setNw2, "new2"]].map(([label, val, setter, tid]) => (
          <div key={tid} className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">{label}</label>
            <input data-testid={`pw-${tid}`} type="password" value={val} onChange={(e) => setter(e.target.value)} required
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
          </div>
        ))}
        <button type="submit" disabled={busy} data-testid="change-pw-submit"
          className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
          {busy && <Loader2 size={15} className="animate-spin" />} Update Password
        </button>
      </form>
    </div>
  );
}

function Empty({ text }) {
  return <div className="mt-8 border border-dashed border-[#E5E1D8] p-12 text-center text-[#58635C]">{text}</div>;
}
